<script setup lang="ts">
/**
 * A table of figures.
 *
 * Set the way a printed financial statement is set: one rule under the header,
 * no vertical rules unless asked for, and figures right-aligned in tabular
 * numerals so the columns line up on the decimal. A table that looks like a
 * spreadsheet is a table nobody trusts in a signed document.
 *
 * How heavily it is ruled, how much air a row gets and whether negatives are
 * red are the **document's** settings first. A report whose tables are each
 * set differently reads as a folder of attachments, which is exactly what a
 * report is meant not to be. A table may still part from them on purpose, its
 * head, colour, lines, rows, a label column or a total row, through its look
 * (`look.ts`); whatever it does not set, it takes from the document.
 *
 * A cell can still be set on its own: aligned, tinted, inked, merged with its
 * neighbours. Those are the choices a house style cannot make for a person,
 * because they say what a particular cell means, a heading centred over the
 * columns it names or a row of figures picked out for the reader. They are
 * painted only where somebody made the choice, and a cell nobody touched keeps
 * following the document.
 *
 * Merged cells are drawn from the table's plan (`spans.ts`), which also says
 * how deep the head is: a heading that runs down over a second line of column
 * labels takes that line into the head, so both repeat on every printed page.
 *
 * The same block draws the preview in the table editor, where it also shows
 * which cells are selected and reports a click on one. Both are off unless
 * asked for, so the page and the printed file never carry either. On the page,
 * a double click on the table opens that editor.
 *
 * What this block does decide is what each cell *is*: a negative, a total —
 * and how tightly the table packs into the page width (`fit`). For `shrink`
 * and `compact`, the table is laid out at its natural width then scaled down
 * to the page measure (Excel's "fit to page"), rather than only shrinking the
 * font while equal columns tear the headers apart.
 *
 * That is read off the value rather than applied to it. Nothing here rewrites
 * a figure, and nothing should: a settings panel that could change a number is
 * a settings panel that can put a wrong one in a signed document.
 */
import { useI18n } from 'vue-i18n'

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { currencySymbol } from '@/domain/layout'
import { cellChanges, type CellDiff } from '@/features/versions/table'
import { emptyCell, type TableCell, type TableSection } from '@/domain/section'
import CellChangeTip from '@/sections/table/CellChangeTip.vue'
import { CELL_RULE, covers, type CellPoint, type CellTarget } from '@/sections/table/edit'
import { shownFigure } from '@/sections/table/figures'
import { lookClasses, lookStyle } from '@/sections/table/look'
import {
  cellAtLine,
  drawnByLine,
  headLines,
  lineCells,
  planOf,
  rowOf,
  type DrawnCell,
} from '@/sections/table/spans'
import { useCellTip } from '@/sections/table/useCellTip'
import { useTableEditor } from '@/sections/table/tableEditor'
import { effectiveLook } from '@/sections/table/tableStyle'
import { useTableStylesStore } from '@/stores/tableStyles'
import { usableColumnWidths } from '@/sections/table/widths'
import { useReportStore } from '@/stores/report'
import { useSnapshotsStore } from '@/stores/snapshots'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    section: TableSection
    /** Cells to show as selected. Only the editor's preview passes any. */
    picked?: CellTarget | null
    /** Whether a click on a cell is reported, which only the preview asks for. */
    pickable?: boolean
  }>(),
  { picked: null, pickable: false },
)

const emit = defineEmits<{
  /** A cell was pressed, with Shift when `extend` is set. */
  pick: [point: CellPoint, extend: boolean]
}>()

const report = useReportStore()
const editor = useTableEditor()

const snapshots = useSnapshotsStore()

/**
 * What changed in this table since the version the report is read against.
 *
 * Marked cell by cell, but not in the editor's preview: accepting or rejecting
 * is still the block's. A table read from CSRD points to CSRD for the history of
 * a figure; one kept here reads it from the journal, and opens it beside the page.
 */
const marks = computed<ReadonlyMap<string, CellDiff> | null>(() => {
  if (props.pickable) return null
  const was = snapshots.wasOf(props.section.id)
  if (!snapshots.baseline || !was || was.kind !== 'table') return null
  return cellChanges(was, props.section)
})

const markedCount = computed(() => marks.value?.size ?? 0)

function markOf(drawn: DrawnCell): CellDiff | undefined {
  return marks.value?.get(`${drawn.line}:${drawn.column}`)
}

const { reading, baseNumber, screenUrl, show, hold, drop, open } = useCellTip(() => props.section)

const frameRef = ref<HTMLElement | null>(null)
const tableRef = ref<HTMLTableElement | null>(null)
const scale = ref(1)
const frameHeight = ref<string | undefined>(undefined)

const fit = computed(() => props.section.fit ?? 'normal')

const columnCount = computed(() =>
  Math.max(props.section.header.length, props.section.rows[0]?.length ?? 0, 1),
)

const appliedWidths = computed(() =>
  usableColumnWidths(props.section.columnWidths, columnCount.value),
)

/**
 * Base type size before any scale-to-fit.
 *
 * `compact` starts smaller so less scaling is needed; `shrink` and `normal`
 * keep the house size and let the measured scale do the work when asked.
 */
const fontSize = computed(() => (fit.value === 'compact' ? '7.5pt' : '9.5pt'))

/**
 * How this table is drawn where it parts from the document's tables: the style
 * of the library it follows, with what it sets itself on top.
 */
const library = useTableStylesStore()
const look = computed(() => effectiveLook(props.section, library.styles))

const tableStyle = computed(() => ({
  '--table-font-size': fontSize.value,
  '--table-scale': String(scale.value),
  ...lookStyle(look.value, report.layout.accent, report.layout.tableHead),
}))

const frameStyle = computed(() => ({
  height: frameHeight.value,
}))

/**
 * What the figures are expressed in, said once above the table.
 *
 * Only where the document has been told. A line reading "En euros" on a report
 * whose author never said so is a statement somebody else made on their
 * behalf, and it may be wrong.
 */
const unit = computed(() => {
  const { figureScale, currency } = report.layout
  if (figureScale === 'none') return ''

  const symbol = currencySymbol(currency)
  return symbol === ''
    ? t(`document.scalesBare.${figureScale}`)
    : t(`document.scales.${figureScale}`, { currency: symbol })
})

const plan = computed(() => planOf(props.section))

/** One line of the table as it is drawn, with its position kept for the key. */
interface DrawnLine {
  readonly line: number
  readonly cells: readonly DrawnCell[]
}

const lines = computed<readonly DrawnLine[]>(() =>
  drawnByLine(plan.value).map((cells, line) => ({ line, cells })),
)

/** How many lines are drawn as the head; none when the header is not drawn. */
const head = computed(() => headLines(props.section, plan.value))

const headRows = computed(() => lines.value.slice(0, head.value))

/** The body starts under the head, or under the header's line when there is no head. */
const bodyRows = computed(() => lines.value.slice(Math.max(head.value, 1)))

/** Whether anything is merged, which is when the rules need the edge classes below. */
const spanned = computed(() => plan.value.drawn.some((one) => one.colspan > 1 || one.rowspan > 1))

function cellOf(drawn: DrawnCell): TableCell {
  return cellAtLine(props.section, drawn.line, drawn.column) ?? emptyCell()
}

/**
 * Whether a cell reads as a negative.
 *
 * Both conventions, because both arrive: a minus sign, and the accounting
 * parentheses that a great many statements use instead. Only ever consulted
 * for a cell already marked as holding a figure, so a line item called
 * "(net)" is not quietly turned red.
 */
function isNegative(cell: TableCell): boolean {
  if (!cell.numeric) return false
  const value = cell.value.trim()
  return value.startsWith('-') || value.startsWith('−') || /^\(.*\)$/.test(value)
}

/**
 * Whether a line is the one the column resolves to.
 *
 * Taken from the emphasis rather than from the words: the importer and the
 * author both mark a total by making it bold, and reading the label would mean
 * keeping a list of the words for it in every language.
 */
function isTotal(line: number): boolean {
  return (lineCells(props.section, line) ?? []).some((cell) => cell.strong)
}

/** What a cell shows: its value, with a negative figure in brackets where the table says so. */
function shown(drawn: DrawnCell): string {
  return shownFigure(cellOf(drawn), look.value.negatives)
}

/**
 * The classes a drawn cell carries.
 *
 * What it is, how somebody set it, and, in a table with merged cells, whether
 * it reaches the right edge or the foot. Those two are said outright because
 * a merged cell is not always the last cell of its row or in the last row,
 * which is what the page's rules otherwise go by.
 */
function classesOf(drawn: DrawnCell, inBody: boolean): (string | Record<string, boolean>)[] {
  const cell = cellOf(drawn)
  return [
    {
      'is-numeric': cell.numeric === true,
      'is-label': cell.numeric !== true,
      'is-strong': cell.strong === true,
      'is-first': drawn.column === 0,
      'is-total': inBody && isTotal(drawn.line),
      'is-negative': inBody && isNegative(cell),
      'is-edge': spanned.value && drawn.column + drawn.colspan === plan.value.columns,
      'is-foot': spanned.value && drawn.line + drawn.rowspan === plan.value.lines,
      'is-moved': markOf(drawn)?.status === 'changed',
      'is-new': markOf(drawn)?.status === 'added',
      'is-picked': props.picked !== null && covers(props.picked, rowOf(drawn.line), drawn.column),
    },
    cell.align ? `is-${cell.align}` : '',
  ]
}

/**
 * The colours somebody chose, inline.
 *
 * Inline because they are the cell's own and the page's stylesheet cannot
 * know them. Safe to write because the reader only ever lets a validated
 * six-digit hex through: see `readCellColour`.
 */
function colourStyle(drawn: DrawnCell): Record<string, string> {
  const cell = cellOf(drawn)
  const style: Record<string, string> = {}
  if (cell.fill) style['background-color'] = cell.fill
  if (cell.ink) style['color'] = cell.ink
  if (cell.ruleAbove) style['border-top'] = CELL_RULE
  if (cell.ruleBelow) style['border-bottom'] = CELL_RULE
  return style
}

/**
 * A press on a cell, in the preview.
 *
 * The press rather than the click, and with the browser's own handling held
 * back, so a Shift and a click extends the selection instead of selecting the
 * words between here and the last place the cursor was.
 */
function press(event: MouseEvent, drawn: DrawnCell): void {
  if (!props.pickable || event.button !== 0) return
  event.preventDefault()
  emit('pick', { row: rowOf(drawn.line), column: drawn.column }, event.shiftKey)
}

/** A double click on the table on the page: the table editor, where there is one to open. */
function openEditor(): void {
  if (!props.pickable && report.editable) editor.open(props.section.id)
}

/** A span worth writing: none for a single cell, so plain tables print as they always did. */
function span(value: number): number | undefined {
  return value > 1 ? value : undefined
}

/**
 * Scale the table down so its natural width fits the page measure.
 *
 * Used for `shrink` and `compact`. `normal` stays at full size so a short
 * table is not quietly reduced. Floor at 0.45 so a pathological sheet stays
 * readable rather than vanishing.
 *
 * `transform: scale` does not affect layout height, so the frame is given an
 * explicit height of natural × scale or the next section slides underneath.
 */
function measureFit(): void {
  const frame = frameRef.value
  const table = tableRef.value
  if (!frame || !table) return

  if (fit.value === 'normal' || appliedWidths.value) {
    scale.value = 1
    frameHeight.value = undefined
    return
  }

  scale.value = 1
  frameHeight.value = undefined

  void nextTick(() => {
    const available = frame.clientWidth
    const natural = table.scrollWidth
    const naturalHeight = table.offsetHeight
    if (available <= 0 || natural <= 0) return

    const next = Math.min(1, Math.max(0.45, available / natural))
    scale.value = next
    frameHeight.value = `${Math.ceil(naturalHeight * next)}px`
  })
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  measureFit()
  if (typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => measureFit())
  if (frameRef.value) resizeObserver.observe(frameRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(
  () =>
    [
      props.section.fit,
      props.section.header,
      props.section.rows,
      props.section.columnWidths,
    ] as const,
  () => nextTick(measureFit),
  { deep: true },
)
</script>

<template>
  <figure
    class="table-figure"
    :class="[`table-figure--${fit}`, { 'table-figure--pickable': pickable }]"
    @dblclick="openEditor"
  >
    <p v-if="unit" class="page-unit">{{ unit }}</p>
    <p v-if="markedCount > 0" class="table-figure__moved">
      {{ t('versions.cell.notes', { count: markedCount, number: baseNumber }) }}
    </p>

    <div ref="frameRef" class="table-figure__frame" :style="frameStyle">
      <table
        ref="tableRef"
        class="table-figure__table"
        :class="[
          {
            'is-ruled': section.ruled,
            'is-spanned': spanned,
            'is-fitting': fit !== 'normal' && !appliedWidths,
            'is-sized': !!appliedWidths,
          },
          lookClasses(look),
        ]"
        :style="tableStyle"
      >
        <colgroup v-if="appliedWidths">
          <col
            v-for="(width, column) in appliedWidths"
            :key="column"
            :style="{ width: `${width}%` }"
          />
        </colgroup>
        <thead v-if="headRows.length > 0">
          <tr v-for="row in headRows" :key="row.line">
            <th
              v-for="drawn in row.cells"
              :key="drawn.column"
              :class="classesOf(drawn, false)"
              :style="colourStyle(drawn)"
              :colspan="span(drawn.colspan)"
              :rowspan="span(drawn.rowspan)"
              :scope="drawn.colspan > 1 ? 'colgroup' : 'col'"
              @mousedown="press($event, drawn)"
              @mouseenter="show($event, markOf(drawn), drawn.line, drawn.column)"
              @mouseleave="drop"
              @click="open(markOf(drawn), drawn.line, drawn.column)"
            >
              {{ shown(drawn) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in bodyRows" :key="row.line">
            <td
              v-for="drawn in row.cells"
              :key="drawn.column"
              :class="classesOf(drawn, true)"
              :style="colourStyle(drawn)"
              :colspan="span(drawn.colspan)"
              :rowspan="span(drawn.rowspan)"
              @mousedown="press($event, drawn)"
              @mouseenter="show($event, markOf(drawn), drawn.line, drawn.column)"
              @mouseleave="drop"
              @click="open(markOf(drawn), drawn.line, drawn.column)"
            >
              {{ shown(drawn) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>

    <CellChangeTip
      v-if="reading"
      :anchor="reading.anchor"
      :cell="reading.cell"
      :number="baseNumber"
      :link="section.csrd"
      :url="screenUrl"
      :last="reading.last"
      :history="!section.csrd"
      @enter="hold"
      @leave="drop"
    />
  </figure>
</template>

<style scoped>
.table-figure {
  margin: 0;
}

.table-figure--pickable :is(th, td) {
  cursor: cell;
}

/* The selection, in the preview only: a ring inside the cell, over whatever
   fill it has, so a coloured cell still reads as selected. */
.table-figure--pickable .is-picked {
  box-shadow:
    inset 0 0 0 2px var(--accent-fill),
    inset 0 0 0 999px color-mix(in srgb, var(--accent-fill) 12%, transparent);
}

/* A figure that changed since the version read against: a tint and a dot, drawn
   as a shadow and a corner so neither moves the figure. The export clears both. */
.table-figure__table :is(.is-moved, .is-new) {
  position: relative;
  background-color: color-mix(in srgb, var(--warn, #d99a00) 16%, transparent);
}

.table-figure__table .is-new {
  background-color: color-mix(in srgb, var(--accent-fill) 12%, transparent);
}

.table-figure__table .is-moved::after {
  content: '';
  position: absolute;
  top: 3px;
  right: 3px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--warn, #d99a00);
}

.table-figure__moved {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--warn, #d99a00);
}

.table-figure__frame {
  width: 100%;
  overflow: hidden;
}

/* Natural width first; scale brings it back inside the page. Height is set in
   JS from natural × scale so the next block does not tuck underneath. */
.table-figure__table.is-fitting {
  width: max-content;
  max-width: none;
  table-layout: auto;
  transform: scale(var(--table-scale, 1));
  transform-origin: top left;
}

/* Author-set shares: fixed layout inside the page measure, no transform. */
.table-figure__table.is-sized {
  width: 100%;
  table-layout: fixed;
  transform: none;
}
</style>
